import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Crown, Medal, Award } from "lucide-react";
import { api } from "../lib/api";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { usePageMeta } from "../hooks/usePageMeta";

const CATEGORIES = [
  { value: "overall", label: "Overall" },
  { value: "friendly", label: "Friendly" },
  { value: "challenge", label: "Challenge" },
  { value: "cup", label: "Cup" },
];

const DIVISIONS = [
  { value: "all", label: "All ages", hint: "Everyone" },
  { value: "junior", label: "Juniors", hint: "Under 12" },
  { value: "senior", label: "Seniors", hint: "12–15" },
  { value: "masters", label: "Masters", hint: "16+" },
];

const podium = [
  { icon: Crown, color: "#FFD700" },
  { icon: Medal, color: "#C0C0C0" },
  { icon: Award, color: "#CD7F32" },
];

export default function LeaderboardPage() {
  usePageMeta(
    "Tournament Leaderboards",
    "Player rankings from every Pokémon League Friendly, Challenge and Cup run at Hex Labs Games in Maldon, Essex."
  );
  const [category, setCategory] = useState("overall");
  const [division, setDivision] = useState("all");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api
      .get(`/leaderboard?category=${category}&division=${division}`)
      .then(({ data }) => setRows(data))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [category, division]);

  return (
    <div className="mx-auto max-w-6xl px-5 lg:px-10 py-10 lg:py-16">
      <p className="font-mono text-xs uppercase tracking-[0.35em] text-primary mb-4">Win 3 · Draw 1 · Loss 0</p>
      <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter">
        LEADER<span className="text-primary">BOARDS</span>
      </h1>
      <p className="mt-4 text-base md:text-lg text-neutral-400 max-w-xl">
        Points earned from every League Friendly, Challenge and Cup. Prerelease events are logged but never ranked.
        Age divisions follow the Pokémon standard — Juniors, Seniors and Masters — taken from each player's birthdate
        in the tournament file, so younger players compete against their own age group.
      </p>

      <Tabs value={category} onValueChange={setCategory} className="mt-8">
        <TabsList className="bg-white/5 border border-white/10 rounded-full flex-wrap h-auto">
          {CATEGORIES.map((c) => (
            <TabsTrigger
              key={c.value}
              value={c.value}
              data-testid={`leaderboard-tab-${c.value}`}
              className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-black text-xs uppercase tracking-wider px-4"
            >
              {c.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <div className="mt-4 flex flex-wrap gap-1" data-testid="division-filters">
        {DIVISIONS.map((d) => (
          <button
            key={d.value}
            data-testid={`division-tab-${d.value}`}
            onClick={() => setDivision(d.value)}
            className={`text-[11px] uppercase tracking-wider px-3 py-1.5 rounded-full border transition-colors duration-200 ${
              division === d.value
                ? "border-primary text-primary bg-primary/10"
                : "border-white/10 text-neutral-500 hover:text-white"
            }`}
          >
            {d.label} <span className="text-neutral-600 normal-case tracking-normal">· {d.hint}</span>
          </button>
        ))}
      </div>

      <div className="mt-8 border border-white/10 rounded-md overflow-hidden" data-testid="leaderboard-table">
        <div className="grid grid-cols-[48px_1fr_repeat(3,minmax(0,68px))] sm:grid-cols-[64px_1fr_repeat(4,minmax(0,90px))] px-3 py-2 text-[10px] uppercase tracking-[0.18em] text-neutral-500 border-b border-white/10 bg-white/[0.03]">
          <div>Rank</div>
          <div>Player</div>
          <div className="text-right">W/D/L</div>
          <div className="text-right hidden sm:block">Events</div>
          <div className="text-right">Win %</div>
          <div className="text-right">Points</div>
        </div>

        {loading && <div className="p-6 text-sm text-neutral-500" data-testid="leaderboard-loading">Loading…</div>}
        {!loading && rows.length === 0 && (
          <div className="p-6 text-sm text-neutral-500" data-testid="leaderboard-empty">
            No ranked results in this bracket yet. Upload .TDF files from the admin dashboard to build the board.
          </div>
        )}

        {rows.map((r, i) => {
          const p = i < 3 ? podium[i] : null;
          return (
            <Link
              key={r.player_id}
              to={`/players/${r.player_id}`}
              data-testid={`leaderboard-row-${r.player_id}`}
              className="grid grid-cols-[48px_1fr_repeat(3,minmax(0,68px))] sm:grid-cols-[64px_1fr_repeat(4,minmax(0,90px))] items-center px-3 py-3 border-b border-white/5 hover:bg-white/[0.04] group transition-colors duration-200 animate-fade-up"
              style={{ animationDelay: `${Math.min(i, 15) * 35}ms` }}
            >
              <div className="flex items-center gap-1.5 font-mono text-sm" style={{ color: p ? p.color : "#A3A3A3" }}>
                {p && <p.icon className="h-4 w-4" />} {r.rank}
              </div>
              <div className="truncate text-sm group-hover:text-primary transition-colors duration-200">
                {r.name}
                {r.division !== "unknown" && (
                  <span className="ml-2 text-[10px] font-mono uppercase text-neutral-600">{r.division}</span>
                )}
              </div>
              <div className="text-right font-mono text-xs">
                <span className="text-win">{r.wins}</span>/<span className="text-draw">{r.draws}</span>/
                <span className="text-loss">{r.losses}</span>
              </div>
              <div className="text-right font-mono text-xs text-neutral-400 hidden sm:block">{r.events}</div>
              <div className="text-right font-mono text-xs text-neutral-400">{r.win_rate}%</div>
              <div className="text-right font-mono text-sm font-bold text-primary">{r.points}</div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
